import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { TranslateService } from '@ngx-translate/core';

import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';

import { UserOutcomeDetail } from '@app/outcomes/outcomes-api.model';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { ProfileService } from '@app/profile/services/profile.service';
import { ProfileCollectionItem } from '@app/profile/profile-api.model';
import { UserOutcomeActionsService } from '@app/profile/components/user-outcome-tile-v2/user-outcome-actions/user-outcome-actions.service';

@Component({
  selector: 'dgx-user-outcome-actions',
  templateUrl: './user-outcome-actions.component.html',
  styleUrls: ['./user-outcome-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserOutcomeActions
  extends SubscriberBaseDirective
  implements OnInit
{
  // Bindings
  @Input() public item: ProfileCollectionItem<UserOutcomeDetail>;
  @Input() public appendToBody: boolean = true;
  @Input() public showSelectOption: boolean = false;

  @Input() public contentType: string;
  @Input() public itemId: number;

  @Input() public placement: PlacementArray = 'bottom-right';

  @Output() public onModalMenu = new EventEmitter<Event>();
  @Output() public itemDeleted = new EventEmitter<UserOutcomeDetail>();
  @Output() public itemEdited = new EventEmitter<UserOutcomeDetail>();
  @Output() public itemSelected = new EventEmitter<
    ProfileCollectionItem<UserOutcomeDetail>
  >();

  public menuConfig: MenuViewModel[];

  public i18n = this.translate.instant([
    'Core_Edit',
    'Core_ReportAProblem',
    'Core_Delete',
    'Core_Select',
    'ProfileCtrl_ExperienceReported',
    'dgProfileOverview_AchievementReported',
  ]);

  @ViewChild('meatball') private meatball: ElementRef;
  constructor(
    private userOutcomeActionsService: UserOutcomeActionsService,
    private translate: TranslateService,
    private notifierService: NotifierService,
    private profileService: ProfileService,
    private profileOverviewActionsService: ProfileOverviewActionsService
  ) {
    super();
  }

  public ngOnInit() {
    this.menuConfig = this.getMenuConfig();
  }

  public getMenuConfig(): MenuViewModel[] {
    const ownerIsViewing = this.profileService.ownerIsViewing;
    return [
      {
        title: this.i18n.Core_Select,
        defaultAction: (event) =>
          this.userOutcomeActionsService.notifyItemSelected(this.item),
        isHidden: () => !ownerIsViewing || !this.showSelectOption,
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_Edit,
        defaultAction: (event) => this.showEditModal(event),
        isHidden: () => !ownerIsViewing,
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_Delete,
        defaultAction: (event) => this.showDeleteModal(),
        isHidden: () => !ownerIsViewing,
        preventRefocus: true,
      },
      {
        title: this.i18n.Core_ReportAProblem,
        defaultAction: (event) =>
          this.showReportModal(event, this.meatball.nativeElement),
        isHidden: () => false,
        preventRefocus: true,
      },
    ];
  }

  public showEditModal(event: Event): void {
    this.onModalMenu.emit();
    this.userOutcomeActionsService.editUserOutcome(
      event,
      this.item.content as UserOutcomeDetail,
      this.itemEdited
    );
  }

  public showDeleteModal(): void {
    const { contentType } = this.item;
    /*
    Including final strings here to allow global search matches

    confirmModal_DeleteEpisodeTitle
    confirmModal_DeleteEpisodeBody
    confirmModal_DeleteDegreeTitle
    confirmModal_DeleteDegreeBody
    confirmModal_DeleteAccomplishmentTitle
    confirmModal_DeleteAccomplishmentBody
    confirmModal_DeleteBadgeTitle
    confirmModal_DeleteBadgeBody
    confirmModal_DeleteAwardTitle
    confirmModal_DeleteAwardBody
    confirmModal_DeleteCertificateTitle
    confirmModal_DeleteCertificateBody
  */

    const headerText = this.translate.instant(
      'confirmModal_Delete' + contentType + 'Title'
    );
    const bodyText = this.translate.instant(
      'confirmModal_Delete' + contentType + 'Body'
    );

    const payload = {
      contentCategory: 'Outcome',
      contentType: this.contentType,
      itemId: this.itemId,
      userItemId: this.itemId,
    };

    const title =
      this.contentType === 'Badge' && !this.item.content?.title
        ? 'Badge'
        : this.item.content.title;
    this.onModalMenu.emit();
    // use setTimeout to prevent modal focus being lost
    setTimeout(() =>
      this.userOutcomeActionsService.showDeleteModal(
        headerText,
        bodyText,
        payload,
        title,
        this.itemDeleted
      )
    );
  }

  public showReportModal(event, refocusElement: HTMLElement): void {
    this.onModalMenu.emit();
    this.profileOverviewActionsService
      .showReportModal(event, this.itemId, this.contentType, refocusElement)
      .subscribe(() => {
        this.notifierService.showSuccess(
          this.i18n.dgProfileOverview_AchievementReported
        );
      });
  }
}
